import React, { useState, useEffect } from "react";
import { styled } from "@material-ui/styles";
import Typography from "../typography";
import {
  Grid,
  Card,
  Divider,
  Box,
  Stepper,
  Step,
  StepLabel,
} from "@material-ui/core";
import Button from "../button";
import { RouteComponentProps } from "@reach/router";
import { injectIntl } from "react-intl";
import { useQuery, useMutation } from "@apollo/client";
import { IPlayer } from "../../types";
import FinalState from "./FinalState";
import {
  QUERY_MATCH,
  START_MATCH,
  CREATE_MATCH_CALL,
  UPDATE_STATUS,
  UPDATE_MATCH_LOCATION,
} from "./MatchQueries";
import messages from "./PageViewMatch.intl";
import { PaperLayout } from "../atoms";
import MatchComponent from "./MatchComponentV2";
import MatchConfigurationCard from "./MatchConfigurationCard";
import { IStyledArguments } from "../../types/styled-arguments";
import PeopleIcon from "@material-ui/icons/People";
import SportsIcon from "@material-ui/icons/Sports";
import InfoIcon from "@material-ui/icons/Info";
import WarningIcon from "@material-ui/icons/Warning";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import HeaderMatchV3 from "./HeaderMatchV3";
import MatchInfoCard from "./MatchInfoCard";
import PlayerSelectionCard from "./PlayerSelectionCard";
import SimpleMatchResult from "./SimpleMatchResult";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import PlaylistAddCheckIcon from "@material-ui/icons/PlaylistAddCheck";

// Styled Components
const PageContainer = styled("div")(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.backgroundGrey,
  minHeight: "100vh",
  padding: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
    paddingTop: theme.spacing(2),
  },
}));

const ContentSection = styled(Card)(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  borderRadius: "16px",
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  [theme.breakpoints.down("sm")]: {
    borderRadius: "12px",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

const SectionTitle = styled(Typography.H5)(({ theme }: IStyledArguments) => ({
  color: theme.palette.primary.main,
  fontWeight: 700,
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.25rem",
    marginBottom: theme.spacing(1.5),
    "& svg": {
      fontSize: "1.5rem",
    },
  },
}));

const ActionButtons = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  gap: theme.spacing(2),
  marginTop: theme.spacing(3),
  justifyContent: "flex-end",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    "& button": {
      width: "100%",
    },
  },
}));

const StyledStepper = styled(Stepper)(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  borderRadius: "16px",
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  border: `1px solid ${theme.palette.extra.borderColor}`,
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    borderRadius: "12px",
    marginBottom: theme.spacing(2),
  },
  "& .MuiStepLabel-root": {
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "center",
    },
  },
  "& .MuiStepLabel-label": {
    marginTop: theme.spacing(1),
    fontWeight: 600,
    fontSize: "0.95rem",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.75rem",
      marginTop: theme.spacing(0.5),
      textAlign: "center",
    },
  },
  "& .MuiStepLabel-label.MuiStepLabel-active": {
    color: theme.palette.primary.main,
    fontWeight: 700,
  },
  "& .MuiStepLabel-label.MuiStepLabel-completed": {
    color: theme.palette.success.main,
    fontWeight: 600,
  },
  "& .MuiStepIcon-root": {
    color: "rgba(0, 0, 0, 0.12)",
    fontSize: "2rem",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.75rem",
    },
  },
  "& .MuiStepIcon-root.MuiStepIcon-active": {
    color: theme.palette.primary.main,
    transform: "scale(1.1)",
    transition: "all 0.3s ease",
  },
  "& .MuiStepIcon-root.MuiStepIcon-completed": {
    color: theme.palette.success.main,
  },
  "& .MuiStepConnector-root": {
    top: "24px",
    left: "calc(-50% + 16px)",
    right: "calc(50% + 16px)",
    [theme.breakpoints.down("sm")]: {
      top: "20px",
    },
  },
  "& .MuiStepConnector-line": {
    borderColor: "rgba(0, 0, 0, 0.12)",
    borderTopWidth: "3px",
    borderRadius: "2px",
  },
  "& .MuiStepConnector-root.MuiStepConnector-active .MuiStepConnector-line": {
    borderColor: theme.palette.primary.main,
  },
  "& .MuiStepConnector-root.MuiStepConnector-completed .MuiStepConnector-line": {
    borderColor: theme.palette.success.main,
  },
}));

const StatsGrid = styled(Grid)(({ theme }: IStyledArguments) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const StatCard = styled(Card)(({ theme }: IStyledArguments) => ({
  padding: theme.spacing(2.5),
  textAlign: "center",
  borderRadius: "16px",
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 16px rgba(16, 21, 26, 0.08)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    borderRadius: "12px",
  },
}));

const StatValue = styled(Typography.H4)(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  color: theme.palette.text.primary,
  fontWeight: 800,
  fontVariantNumeric: "tabular-nums",
  marginBottom: "4px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.5rem",
    marginBottom: "2px",
  },
}));

const StatLabel = styled(Typography.Body)(({ theme }: IStyledArguments) => ({
  color: theme.palette.text.secondary,
  fontSize: "0.875rem",
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.75rem",
  },
}));

const InstructionsCard = styled(Box)(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.accentSoft,
  borderRadius: "16px",
  padding: theme.spacing(2.5),
  marginBottom: theme.spacing(3),
  border: `1px solid ${theme.palette.extra.borderColor}`,
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    gap: theme.spacing(1.5),
    borderRadius: "12px",
  },
}));

const InstructionIcon = styled(Box)(({ theme }: IStyledArguments) => ({
  width: "48px",
  height: "48px",
  borderRadius: "12px",
  background: theme.palette.primary.main,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "white",
  flexShrink: 0,
  "& svg": {
    fontSize: "28px",
  },
  [theme.breakpoints.down("sm")]: {
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    "& svg": {
      fontSize: "22px",
    },
  },
}));

const InstructionText = styled(Box)({
  flex: 1,
});

const InstructionTitle = styled(Typography.Subtitle1)(({ theme }: IStyledArguments) => ({
  fontWeight: 700,
  color: theme.palette.primary.main,
  marginBottom: "6px",
  fontSize: "1.1rem",
  [theme.breakpoints.down("sm")]: {
    fontSize: "1rem",
    marginBottom: "4px",
  },
}));

const InstructionDescription = styled(Typography.Body)(({ theme }: IStyledArguments) => ({
  color: theme.palette.text.secondary,
  fontSize: "0.875rem",
  lineHeight: 1.6,
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.8rem",
    lineHeight: 1.5,
  },
}));

const PlayerListContainer = styled(Box)(({ theme }: IStyledArguments) => ({
  marginTop: "24px",
  [theme.breakpoints.down("sm")]: {
    marginTop: "16px",
  },
}));

const ListHeader = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(2),
  padding: `0 ${theme.spacing(1)}px`,
  [theme.breakpoints.down("sm")]: {
    marginBottom: theme.spacing(1.5),
    padding: `0 ${theme.spacing(0.5)}px`,
  },
}));

const ListTitle = styled(Typography.Subtitle1)(({ theme }: IStyledArguments) => ({
  fontWeight: 700,
  color: theme.palette.text.primary,
  [theme.breakpoints.down("sm")]: {
    fontSize: "1rem",
  },
}));

const SelectionCounter = styled(Box)(({ theme }: IStyledArguments) => ({
  background: theme.palette.primary.main,
  color: "white",
  borderRadius: "20px",
  padding: "6px 18px",
  fontSize: "0.9rem",
  fontWeight: 700,
  [theme.breakpoints.down("sm")]: {
    padding: "4px 14px",
    fontSize: "0.8rem",
  },
}));

const EmptyState = styled(Box)(({ theme }: IStyledArguments) => ({
  textAlign: "center",
  padding: theme.spacing(6),
  color: theme.palette.text.secondary,
}));

const EmptyStateIcon = styled(Box)(({ theme }: IStyledArguments) => ({
  fontSize: "64px",
  marginBottom: theme.spacing(2),
  opacity: 0.3,
}));

const AlertCard = styled(Box)(({ theme }: IStyledArguments) => ({
  borderRadius: "16px",
  padding: theme.spacing(2.5),
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  border: `1px solid ${theme.palette.extra.borderColor}`,
  "& .alert-icon": {
    fontSize: "28px",
    flexShrink: 0,
  },
  "& .alert-text": {
    fontSize: "0.95rem",
    fontWeight: 600,
    lineHeight: 1.5,
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    gap: theme.spacing(1.5),
    borderRadius: "12px",
    "& .alert-icon": {
      fontSize: "24px",
    },
    "& .alert-text": {
      fontSize: "0.85rem",
    },
  },
}));

const WarningAlert = styled(AlertCard)(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.warningSoft,
  "& .alert-icon": {
    color: theme.palette.extra.warning,
  },
  "& .alert-text": {
    color: theme.palette.extra.warning,
  },
}));

const InfoAlert = styled(AlertCard)(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.infoSoft,
  "& .alert-icon": {
    color: theme.palette.primary.main,
  },
  "& .alert-text": {
    color: theme.palette.primary.dark,
  },
}));

const SuccessAlert = styled(AlertCard)(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.successSoft,
  "& .alert-icon": {
    color: theme.palette.extra.success,
  },
  "& .alert-text": {
    color: theme.palette.extra.success,
  },
}));

const ModeChooserTitle = styled("h3")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 600,
  fontSize: "1.15rem",
  color: theme.palette.text.primary,
  margin: 0,
  marginBottom: theme.spacing(2.5),
  textAlign: "center",
}));

const ModeGrid = styled("div")(({ theme }: IStyledArguments) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
  },
}));

const ModeCard = styled("button")(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  borderRadius: 14,
  padding: theme.spacing(3),
  textAlign: "center",
  cursor: "pointer",
  transition: "all 0.2s ease",
  fontFamily: theme.typography.fontFamily,
  "&:hover": {
    borderColor: theme.palette.primary.main,
    boxShadow: "0 4px 16px rgba(16, 21, 26, 0.1)",
  },
}));

const ModeIcon = styled("div")(({ theme }: IStyledArguments) => ({
  width: 52,
  height: 52,
  borderRadius: "50%",
  background: theme.palette.extra.accentSoft,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto",
  marginBottom: theme.spacing(1.5),
  "& svg": {
    fontSize: "1.6rem",
    color: theme.palette.primary.dark,
  },
}));

const ModeTitle = styled("div")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 600,
  fontSize: "1.05rem",
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(0.75),
}));

const ModeDescription = styled("div")(({ theme }: IStyledArguments) => ({
  fontSize: "0.85rem",
  lineHeight: 1.5,
  color: theme.palette.text.secondary,
}));

const getStepStatus = (matchStatus: string): number => {
  switch (matchStatus) {
    case "PREPARING":
      return 0;
    case "READY":
      return 1;
    case "ONGOING":
      return 2;
    case "FINISHED":
      return 3;
    default:
      return 0;
  }
};

const PageViewMatchV3 = injectIntl<{ id: string } & RouteComponentProps>(
  ({ id, intl }) => {
    const { data, refetch } = useQuery(QUERY_MATCH, {
      variables: { id },
    });

    const [state, setState] = useState<{ [index: string]: boolean }>({});
    const [match, setMatch] = useState<any>();
    const [matchStatus, setMatchStatus] = useState("");
    const [score, setScore] = useState({ home: 0, away: 0 });
    const [scorerList, setScorerList] = useState<{ [index: string]: number }>({});
    const [lineUp, setLineUp] = useState<any>();
    const [lineUpCompleted, setLineUpCompleted] = useState<boolean>(false);
    const [selectedPlayers, setSelectedPlayers] = useState<number>(0);
    const [entryMode, setEntryMode] = useState<null | "simple" | "full">(null);

    const [createCall] = useMutation(CREATE_MATCH_CALL);
    const [startMatch] = useMutation(START_MATCH);
    const [updateStatus] = useMutation(UPDATE_STATUS);
    const [updateMatchLocation] = useMutation(UPDATE_MATCH_LOCATION);

    // Initialize player selection state
    useEffect(() => {
      if (data?.match) {
        const selected: { [index: string]: boolean } = {};
        
        for (let player of data.match.homeTeam.squad) {
          selected[player.id] = false;
        }

        for (let player of data.match.playersConfirmed) {
          selected[player.id] = true;
        }

        setState(selected);
        setMatchStatus(data.match.status);
        setScore(data.match.score);

        // Process scorer list
        const mapScorerList: { [index: string]: number } = {};
        if (data.match.scorerList) {
          for (let scorerFromDB of data.match.scorerList) {
            const key = scorerFromDB.player.id;
            if (!mapScorerList[key]) {
              mapScorerList[key] = scorerFromDB.goals;
            }
          }
          setScorerList(mapScorerList);
        }

        setMatch(data.match);

        // Process lineup
        if (data.match.lineUp) {
          const lineUpMapped: any = {};
          data.match.lineUp.forEach((entry: any) => {
            lineUpMapped[entry.position] = entry.player;
          });
          setLineUp(lineUpMapped);
        }
      }
    }, [data]);

    // Calculate selected players in lineup
    useEffect(() => {
      if (lineUp) {
        const selected = calculatePlayerSelected();
        setSelectedPlayers(selected);
        if (match?.config && selected === match.config.numPlayers) {
          setLineUpCompleted(true);
        }
      }
    }, [lineUp, match]);

    // Update match status
    useEffect(() => {
      if (matchStatus && id) {
        updateStatus({
          variables: {
            matchId: id,
            status: matchStatus,
          },
        });
      }
    }, [matchStatus, id]);

    const calculatePlayerSelected = () => {
      if (!lineUp) return 0;
      return Object.keys(lineUp).filter((key) => lineUp[key] !== "").length;
    };

    const handlePlayerToggleSimple = (playerId: string) => {
      setState({ ...state, [playerId]: !state[playerId] });
    };

    const handleCreateCall = async () => {
      const availablePlayers = Object.keys(state).filter((key) => state[key]);
      
      await createCall({
        variables: {
          matchId: id,
          availablePlayers,
        },
      });

      await refetch();
      
      // After confirming players, update status to READY to allow progression
      setMatchStatus("READY");
    };

    const handleStartMatch = async () => {
      try {
        await startMatch({
          variables: { matchId: id },
        });
        await refetch();
        setMatchStatus("ONGOING");
      } catch (error) {
        console.error("Error starting match:", error);
      }
    };

    const handleLocationUpdate = async (location: string) => {
      try {
        await updateMatchLocation({
          variables: {
            matchId: id,
            location,
          },
        });
        refetch();
      } catch (error) {
        console.error("Error updating location:", error);
      }
    };

    const handleBack = (status: string) => {
      setMatchStatus(status);
    };

    // Calculate currently selected players from state
    const currentlySelectedCount = Object.keys(state).filter((key) => state[key]).length;
    const confirmedCount = match?.playersConfirmed?.length || 0;
    const totalPlayers = match?.homeTeam?.squad?.length || 0;
    
    // Use the higher count between currently selected and confirmed
    const activeCount = Math.max(currentlySelectedCount, confirmedCount);

    // Render match in ONGOING state
    if (match && matchStatus === "ONGOING") {
      return (
        <MatchComponent
          lineUp={lineUp}
          matchConfiguration={match.config}
          team={match.playersConfirmed}
          match={match}
          matchId={id}
          callback={(status: string) => setMatchStatus(status)}
        />
      );
    }

    return (
      <PaperLayout>
        <PageContainer>
          {match && (
            <>
              {/* Header */}
              <HeaderMatchV3 
                match={match} 
                score={score} 
                matchStatus={matchStatus}
                scorerList={scorerList}
              />

              {/* Progress Stepper - only for the full flow */}
              {(matchStatus !== "PREPARING" || entryMode === "full") && (
                <ContentSection>
                  <StyledStepper activeStep={getStepStatus(matchStatus)} alternativeLabel>
                    <Step>
                      <StepLabel>Preparation</StepLabel>
                    </Step>
                    <Step>
                      <StepLabel>Configuration</StepLabel>
                    </Step>
                    <Step>
                      <StepLabel>In Progress</StepLabel>
                    </Step>
                    <Step>
                      <StepLabel>Finished</StepLabel>
                    </Step>
                  </StyledStepper>
                </ContentSection>
              )}

              {/* Match Info Card */}
              <MatchInfoCard match={match} onLocationUpdate={handleLocationUpdate} />

              {/* PREPARING - Mode chooser: quick result vs full match */}
              {matchStatus === "PREPARING" && entryMode === null && (
                <ContentSection>
                  <ModeChooserTitle>
                    {intl.formatMessage(messages.chooseModeTitle)}
                  </ModeChooserTitle>
                  <ModeGrid>
                    <ModeCard
                      type="button"
                      data-testid="mode-simple"
                      onClick={() => setEntryMode("simple")}
                    >
                      <ModeIcon>
                        <FlashOnIcon />
                      </ModeIcon>
                      <ModeTitle>
                        {intl.formatMessage(messages.quickModeTitle)}
                      </ModeTitle>
                      <ModeDescription>
                        {intl.formatMessage(messages.quickModeDescription)}
                      </ModeDescription>
                    </ModeCard>
                    <ModeCard
                      type="button"
                      data-testid="mode-full"
                      onClick={() => setEntryMode("full")}
                    >
                      <ModeIcon>
                        <PlaylistAddCheckIcon />
                      </ModeIcon>
                      <ModeTitle>
                        {intl.formatMessage(messages.fullModeTitle)}
                      </ModeTitle>
                      <ModeDescription>
                        {intl.formatMessage(messages.fullModeDescription)}
                      </ModeDescription>
                    </ModeCard>
                  </ModeGrid>
                </ContentSection>
              )}

              {/* PREPARING - Quick result mode */}
              {matchStatus === "PREPARING" && entryMode === "simple" && (
                <SimpleMatchResult
                  matchId={id}
                  match={match}
                  onBack={() => setEntryMode(null)}
                  onSaved={async () => {
                    // El estado real lo fija el refetch: si el guardado no
                    // hubiera cuajado, la UI no debe decir que terminó.
                    const { data: fresh } = await refetch();
                    setMatchStatus(fresh?.match?.status || "FINISHED");
                    setEntryMode(null);
                  }}
                />
              )}

              {/* PREPARING State - Player Selection (full flow) */}
              {matchStatus === "PREPARING" && entryMode === "full" && (
                <ContentSection>
                  <SectionTitle>
                    <PeopleIcon />
                    {intl.formatMessage(messages.selectPlayers)}
                  </SectionTitle>
                  
                  <Divider style={{ marginBottom: "24px" }} />

                  {/* Instructions Card */}
                  <InstructionsCard>
                    <InstructionIcon>
                      <InfoIcon />
                    </InstructionIcon>
                    <InstructionText>
                      <InstructionTitle>
                        Select players who will attend the match
                      </InstructionTitle>
                      <InstructionDescription>
                        Toggle the switch for each player who confirmed their attendance. 
                        Once all players are selected, you can continue with the lineup configuration.
                      </InstructionDescription>
                    </InstructionText>
                  </InstructionsCard>

                  {/* Stats Grid */}
                  <StatsGrid container spacing={2}>
                    <Grid item xs={6} sm={4}>
                      <StatCard>
                        <StatValue>{activeCount}</StatValue>
                        <StatLabel>Confirmed Players</StatLabel>
                      </StatCard>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <StatCard>
                        <StatValue>{totalPlayers}</StatValue>
                        <StatLabel>Total Squad</StatLabel>
                      </StatCard>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <StatCard>
                        <StatValue>{match.time}</StatValue>
                        <StatLabel>Start Time</StatLabel>
                      </StatCard>
                    </Grid>
                  </StatsGrid>

                  {/* Player Selection List */}
                  <PlayerListContainer>
                    <ListHeader>
                      <ListTitle>Player List</ListTitle>
                      <SelectionCounter>
                        {activeCount} / {totalPlayers}
                      </SelectionCounter>
                    </ListHeader>

                    {match.homeTeam?.squad && match.homeTeam.squad.length > 0 ? (
                      match.homeTeam.squad.map((player: IPlayer) => (
                        <PlayerSelectionCard
                          key={player.id}
                          player={player}
                          selected={state[player.id] || false}
                          onToggle={handlePlayerToggleSimple}
                        />
                      ))
                    ) : (
                      <EmptyState>
                        <EmptyStateIcon>
                          <PeopleIcon style={{ fontSize: "inherit" }} />
                        </EmptyStateIcon>
                        <Typography.Body>
                          No players available in the squad
                        </Typography.Body>
                      </EmptyState>
                    )}
                  </PlayerListContainer>

                  {/* Validation Alerts */}
                  {activeCount === 0 && (
                    <WarningAlert>
                      <WarningIcon className="alert-icon" />
                      <Typography.Body className="alert-text">
                        You must select at least one player to continue
                      </Typography.Body>
                    </WarningAlert>
                  )}

                  {activeCount > 0 && activeCount < 5 && (
                    <InfoAlert>
                      <InfoIcon className="alert-icon" />
                      <Typography.Body className="alert-text">
                        We recommend selecting at least 5 players for the match
                      </Typography.Body>
                    </InfoAlert>
                  )}

                  {activeCount >= 5 && (
                    <SuccessAlert>
                      <CheckCircleIcon className="alert-icon" />
                      <Typography.Body className="alert-text">
                        Perfect! You have {activeCount} confirmed players. You can continue.
                      </Typography.Body>
                    </SuccessAlert>
                  )}

                  <ActionButtons>
                    <Button
                      color="default"
                      variant="outlined"
                      size="large"
                      onClick={() => setEntryMode(null)}
                    >
                      {intl.formatMessage(messages.quickResultBack)}
                    </Button>
                    <Button
                      onClick={handleCreateCall}
                      variant="contained"
                      color="primary"
                      size="large"
                      disabled={activeCount === 0}
                    >
                      {intl.formatMessage(messages.markConfirmed)}
                    </Button>
                  </ActionButtons>
                </ContentSection>
              )}

              {/* READY State - Lineup Configuration */}
              {matchStatus === "READY" && (
                <ContentSection>
                  <SectionTitle>
                    <SportsIcon />
                    Lineup Configuration
                  </SectionTitle>
                  
                  <Divider style={{ marginBottom: "24px" }} />

                  {/* Progress Info - Only show if config exists */}
                  {match.config && (
                    <Box mb={3}>
                      <Typography.Subtitle1 style={{ marginBottom: "16px" }}>
                        Selected players: {selectedPlayers}/{match.config.numPlayers}
                      </Typography.Subtitle1>
                      <Typography.Caption style={{ color: "#666", display: "block", marginBottom: "8px" }}>
                        {confirmedCount} players confirmed • {selectedPlayers} positioned in lineup
                      </Typography.Caption>
                      <Box 
                        style={{ 
                          width: "100%", 
                          height: "8px", 
                          background: "#e0e0e0", 
                          borderRadius: "4px",
                          overflow: "hidden",
                        }}
                      >
                        <Box 
                          style={{ 
                            width: `${(selectedPlayers / match.config.numPlayers) * 100}%`,
                            height: "100%",
                            background: lineUpCompleted ? "#4caf50" : "#2196f3",
                            transition: "width 0.3s ease",
                          }}
                        />
                      </Box>
                    </Box>
                  )}

                  {/* Info message if no config yet */}
                  {!match.config && (
                    <Box mb={3} p={2} style={{ 
                      background: "rgba(33, 150, 243, 0.1)", 
                      borderRadius: "8px",
                      border: "1px solid rgba(33, 150, 243, 0.3)"
                    }}>
                      <Typography.Body style={{ color: "#1976d2", display: "flex", alignItems: "center", gap: "8px" }}>
                        <InfoIcon />
                        Configure the match settings below to start building your lineup with {confirmedCount} confirmed players.
                      </Typography.Body>
                    </Box>
                  )}

                  <MatchConfigurationCard
                    id={id}
                    lineUp={lineUp}
                    matchConfiguration={match.config}
                    onChange={setLineUp}
                    onConfigured={refetch}
                    team={match.playersConfirmed}
                  />

                  <ActionButtons>
                    <Button
                      color="default"
                      onClick={() => handleBack("PREPARING")}
                      variant="outlined"
                      size="large"
                    >
                      {intl.formatMessage(messages.backToPrepare)}
                    </Button>
                    <Button
                      onClick={handleStartMatch}
                      disabled={!lineUpCompleted}
                      variant="contained"
                      color="primary"
                      size="large"
                    >
                      {intl.formatMessage(messages.startGame)}
                    </Button>
                  </ActionButtons>
                </ContentSection>
              )}

              {/* FINISHED State */}
              {matchStatus === "FINISHED" && (
                <>
                  {/* Final State Component */}
                  <ContentSection>
                    <FinalState id={id} match={match} scorerList={scorerList} />
                  </ContentSection>
                </>
              )}
            </>
          )}
        </PageContainer>
      </PaperLayout>
    );
  }
);

export default PageViewMatchV3;
